import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { myToaster } from '@interstellar-component'
import { WorkforceService } from './service'

const WorkforceContext = createContext()

function formatIDR(n) {
  const v = Number(n) || 0
  return `Rp ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function deriveBankCode(provider) {
  if (!provider) return 'XX'
  const words = String(provider).replace(/^PT\.?\s+/i, '').split(/\s+/).filter(Boolean)
  return (words[0] ?? 'XX').slice(0, 3).toUpperCase()
}

const SORT_FIELD_TO_API = {
  full_name: 'full_name',
  code: 'code',
  category: 'category',
  consent_status: 'consent_status',
  consent_expiry: 'consent_expiry_at',
  consent_expiry_at: 'consent_expiry_at',
  created_at: 'created_at',
}

const CATEGORY_TAB_TO_API = {
  'All category': null,
  Employee: 'employee',
  Candidate: 'candidate',
}

function withSelectionRow(item) {
  if (!item || typeof item !== 'object') return null
  return { ...item, checked: Boolean(item.checked) }
}

function WorkforceProvider({ children }) {
  const [workforce, setWorkforce] = useState({
    data: [],
    meta: {},
    filter: [],
    loading: false,
  })

  // Detail responses are now keyed by mode. The backend supports
  // ?type=report (default) and ?type=personal_information; the FE used to
  // make a single fetch and reach into employee.credit_report — that wrapper
  // is gone, and personal_information returns a different sub-shape entirely.
  const [workforceDetailReport, setWorkforceDetailReport] = useState(null)
  const [workforceDetailPersonal, setWorkforceDetailPersonal] = useState(null)
  const [workforceDetailRiskSignals, setWorkforceDetailRiskSignals] = useState(null)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [isLoadingPersonal, setIsLoadingPersonal] = useState(false)
  const [isLoadingRiskSignals, setIsLoadingRiskSignals] = useState(false)

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    filter: [],
    sort: null,
    order: null,
    category: 'All category',
  })

  // Stacked slider state (kept from original: credit/loan sliders stack on top)
  const [sliderStack, setSliderStack] = useState([])
  const [activeAccountId, setActiveAccountId] = useState(null)

  const pushSlider = useCallback((slider) => {
    setSliderStack((prev) => [...prev, slider])
  }, [])

  const popSlider = useCallback(() => {
    setSliderStack((prev) => {
      const newStack = prev.slice(0, -1)
      if (newStack.length === 1) setActiveAccountId(null)
      return newStack
    })
  }, [])

  const handleAccountDetail = useCallback((acc) => {
    setActiveAccountId(acc.id)
    setSliderStack((prev) => {
      const base = prev[0]
      const detail = { current: 'loan-account-detail', data: acc }
      return [base, detail]
    })
  }, [])

  const handleCurrentSlider = useCallback((value) => {
    if (value === null) {
      setSliderStack([])
      setActiveAccountId(null)
    } else {
      setSliderStack([value])
      setActiveAccountId(null)
    }
  }, [])

  const getWorkforce = useCallback(async () => {
    const limit = params.limit ?? 10
    const sortApi = params.sort ? SORT_FIELD_TO_API[params.sort] ?? params.sort : null
    const categoryApi = CATEGORY_TAB_TO_API[params.category] ?? null
    const query = {
      page: params.page,
      limit,
      ...(params.search ? { search: params.search } : {}),
      ...(Array.isArray(params.filter) && params.filter.length > 0 ? { filter: params.filter } : {}),
      ...(sortApi ? { sort: sortApi } : {}),
      ...(params.order ? { order: params.order } : {}),
      ...(categoryApi ? { category: categoryApi } : {}),
    }
    setWorkforce((s) => ({ ...s, loading: true }))
    try {
      const res = await WorkforceService.getWorkforce(query)
      const rawRows = Array.isArray(res?.data) ? res.data : []
      const rows = rawRows.map((r) => withSelectionRow(r)).filter(Boolean)
      const rawMeta = res?.meta
      const perPageForMeta = Number(rawMeta?.per_page ?? rawMeta?.limit ?? limit) || limit
      const meta =
        rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)
          ? {
              ...rawMeta,
              total: Number(rawMeta.total ?? rows.length),
              total_page:
                rawMeta.total_page ??
                rawMeta.last_page ??
                Math.max(1, Math.ceil(Number(rawMeta.total ?? rows.length) / perPageForMeta)),
            }
          : {
              current_page: 1,
              per_page: limit,
              total: rows.length,
              total_page: Math.max(1, Math.ceil(rows.length / perPageForMeta)),
            }
      setWorkforce({
        data: rows,
        meta,
        filter: res?.filter ?? [],
        loading: false,
      })
    } catch (e) {
      myToaster(e)
      setWorkforce((s) => ({ ...s, loading: false }))
    }
  }, [params])

  useEffect(() => {
    getWorkforce()
  }, [getWorkforce])

  // `enquiryId` (optional) pins the Report + Risk Signals tabs to a specific
  // enquiry's snapshot. Personal Information is intentionally never pinned —
  // it always reflects the current workforce row regardless of which enquiry
  // the user has selected in the switcher.
  const fetchWorkforceDetail = useCallback(async (id, mode = 'report', enquiryId = null) => {
    if (id == null || id === '') return
    const config = (() => {
      if (mode === 'personal_information') {
        return {
          setLoading: setIsLoadingPersonal,
          setSlot: setWorkforceDetailPersonal,
          query: { type: 'personal_information' },
        }
      }
      if (mode === 'risk_signals') {
        return {
          setLoading: setIsLoadingRiskSignals,
          setSlot: setWorkforceDetailRiskSignals,
          query: { type: 'risk_signals', ...(enquiryId ? { enquiryId } : {}) },
        }
      }
      return {
        setLoading: setIsLoadingReport,
        setSlot: setWorkforceDetailReport,
        query: enquiryId ? { enquiryId } : {},
      }
    })()
    const { setLoading, setSlot, query } = config
    setLoading(true)
    try {
      const res = await WorkforceService.getWorkforceDetail(id, query)
      setSlot(res?.data !== undefined ? res.data : res)
    } catch (e) {
      myToaster(e)
      setSlot(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleWorkforceSort = useCallback(({ sort, order }) => {
    setParams((p) => ({ ...p, page: 1, sort: sort ?? null, order: order ?? null }))
  }, [])

  const handleWorkforceSelectionChange = useCallback((updated) => {
    setWorkforce((prev) => ({ ...prev, data: updated.data }))
  }, [])

  const setPage = useCallback((page) => {
    setParams((p) => ({ ...p, page }))
  }, [])

  const setSearchTerm = useCallback((search) => {
    setParams((p) => ({ ...p, page: 1, search }))
  }, [])

  const setSelectedCategory = useCallback((category) => {
    setParams((p) => ({ ...p, page: 1, category }))
  }, [])

  const getEmployeeById = useCallback(
    (id) => {
      if (id == null) return null
      const rows = Array.isArray(workforce?.data) ? workforce.data : []
      return rows.find((r) => String(r.id) === String(id)) ?? null
    },
    [workforce?.data]
  )

  const workforceRows = useMemo(
    () => (Array.isArray(workforce?.data) ? workforce.data : []),
    [workforce?.data]
  )

  // Six product cards keyed off backend's loan_category bucket aggregation.
  // Schema v15+ ships count + total_outstanding + worst_kol + accounts per
  // bucket; pre-v15 snapshots return all-null buckets and the cards render
  // empty (no demos — show the truth).
  const LOAN_CATEGORY_META = [
    { key: 'credit_card', title: 'Credit Card',                          icon: 'CoinsStacked03', color: 'Success' },
    { key: 'paylater',    title: 'Paylater',                             icon: 'ShoppingBag03',  color: 'Blue'    },
    { key: 'kkb',         title: 'KKB', subtitle: '(Motor vehicle loan)',  icon: 'File06',         color: 'Warning' },
    { key: 'kpr',         title: 'KPR', subtitle: '(Home ownership loan)', icon: 'Shield02',       color: 'Orange'  },
    { key: 'kta',         title: 'KTA', subtitle: '(Unsecured loan)',      icon: 'DotsVertical',   color: 'Gray'    },
    { key: 'other',       title: 'Other',                                icon: 'DotsVertical',   color: 'Gray'    },
  ]

  const loanCategories = useMemo(() => {
    const lc = workforceDetailReport?.loan_category || {}
    return LOAN_CATEGORY_META.map((b) => {
      const bucket = lc[b.key] || {}
      const count = Number(bucket.count) || 0
      const total = Number(bucket.total_outstanding) || 0
      const worstKol = bucket.worst_kol == null ? null : Number(bucket.worst_kol)
      return {
        title: b.title,
        subtitle: b.subtitle ?? null,
        accountCount: count > 0 ? `${count} account${count === 1 ? '' : 's'}` : 'No active loan',
        amount: formatIDR(total),
        // Per-bucket worst KOL beats the overall summary KOL — empty buckets
        // get no badge so we don't claim "Kol 3" on a category the subject
        // doesn't have.
        kolBadge: worstKol != null ? `KOL ${worstKol}` : null,
        color: b.color,
        icon: b.icon,
      }
    })
  }, [workforceDetailReport?.loan_category])

  // Per-bucket account list keyed by card title — feeds LoanCategorySlider's
  // account list. Each entry preserves the full backend account shape under
  // `raw` so LoanAccountSlider can pull profile-derived blocks (payment
  // history grid, collectibility history) without an extra fetch.
  const loanAccounts = useMemo(() => {
    const lc = workforceDetailReport?.loan_category || {}
    const out = {}
    for (const b of LOAN_CATEGORY_META) {
      const bucket = lc[b.key] || {}
      const accounts = Array.isArray(bucket.accounts) ? bucket.accounts : []
      out[b.title] = accounts.map((a, i) => ({
        id: a.loan_id || `${b.key}-${i}`,
        bank: deriveBankCode(a.provider),
        name: a.provider || '—',
        kol: a.kol != null ? `Kol ${a.kol}` : null,
        label: 'Loan amount',
        amount: formatIDR(a.debit_balance),
        isActive: (a.contract_phase || '').toLowerCase().includes('active'),
        raw: a,
      }))
    }
    return out
  }, [workforceDetailReport?.loan_category])

  const workforceMeta = useMemo(() => {
    const m = workforce?.meta
    if (m && typeof m === 'object' && !Array.isArray(m)) return m
    return {
      current_page: 1,
      per_page: params.limit ?? 10,
      total: workforceRows.length,
      total_page: 1,
    }
  }, [workforce?.meta, workforceRows.length, params.limit])

  const currentSlider = useMemo(
    () => (sliderStack.length > 0 ? sliderStack[sliderStack.length - 1] : null),
    [sliderStack]
  )

  const contextValue = useMemo(
    () => ({
      params,
      setParams,
      workforce,
      workforceRows,
      workforceMeta,
      getWorkforce,
      handleWorkforceSort,
      handleWorkforceSelectionChange,
      workforceDetailReport,
      workforceDetailPersonal,
      workforceDetailRiskSignals,
      // Backwards-compat shim: most of the section files still pull
      // `workforceDetail` and we'll migrate them in this same change.
      // Once every consumer reads workforceDetailReport directly we can drop
      // this alias.
      workforceDetail: workforceDetailReport,
      isLoadingReport,
      isLoadingPersonal,
      isLoadingRiskSignals,
      isLoadingWorkforceDetail: isLoadingReport,
      fetchWorkforceDetail,
      searchTerm: params.search,
      setSearchTerm,
      selectedCategory: params.category,
      setSelectedCategory,
      sortField: params.sort,
      sortOrder: params.order,
      setPage,
      getEmployeeById,
      // Stacked sliders
      sliderStack,
      currentSlider,
      pushSlider,
      popSlider,
      handleCurrentSlider,
      handleAccountDetail,
      activeAccountId,
      loanCategories,
      loanAccounts,
    }),
    [
      params,
      workforce,
      workforceRows,
      workforceMeta,
      getWorkforce,
      handleWorkforceSort,
      handleWorkforceSelectionChange,
      workforceDetailReport,
      workforceDetailPersonal,
      workforceDetailRiskSignals,
      isLoadingReport,
      isLoadingPersonal,
      isLoadingRiskSignals,
      fetchWorkforceDetail,
      setSearchTerm,
      setSelectedCategory,
      setPage,
      getEmployeeById,
      loanCategories,
      loanAccounts,
      sliderStack,
      currentSlider,
      pushSlider,
      popSlider,
      handleCurrentSlider,
      handleAccountDetail,
      activeAccountId,
    ]
  )

  return <WorkforceContext.Provider value={contextValue}>{children}</WorkforceContext.Provider>
}

const useWorkforce = () => {
  const context = useContext(WorkforceContext)
  if (context === undefined) {
    throw new Error('useWorkforce must be used within a WorkforceProvider')
  }
  return context
}

export { WorkforceProvider, useWorkforce }
